import React, { ForwardedRef } from 'react';

import styles from './InputComponent.module.css';

interface InputComponentProps {
  value?: string;
  type?: string;
  left?: React.ReactNode;
  right?: React.ReactNode;
  placeholder?: string;
  required?: boolean;
  label?: string | React.ReactElement;
  setValue?: (value: string, fieldName?: string | null) => void;
  error?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  readOnly?: boolean;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  onClick?: React.MouseEventHandler<HTMLInputElement>;
  size?: 'normal' | 'large';
  fieldName?: string | null;
  style?: React.CSSProperties;
  focusStyle?: boolean;
  labelRight?: React.ReactNode;
  inputStyle?: React.CSSProperties;
  inputProps?: React.InputHTMLAttributes<HTMLInputElement>;
  inputWrapperStyle?: React.CSSProperties;
  inputClassName?: string;
  inputWrapperClass?: string;
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  labelStyle?: React.CSSProperties;
  onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
  containerClassName?: string;
}

function InputComponent(
  {
    value,
    type = 'text',
    left,
    right,
    placeholder,
    required,
    label,
    setValue = () => {},
    error,
    disabled,
    autoFocus,
    onFocus,
    readOnly = false,
    onBlur,
    onClick,
    size = 'normal',
    fieldName = null,
    style,
    focusStyle = true,
    labelRight,
    inputStyle,
    inputProps,
    inputWrapperStyle,
    inputClassName,
    inputWrapperClass,
    onChange,
    labelStyle,
    onKeyDown,
    containerClassName,
  }: InputComponentProps,
  inputRef: ForwardedRef<HTMLInputElement>
) {
  return (
    <div
      style={style}
      className={`${styles.customInputContainer} ${
        size === 'large' ? styles.customInputContainerLarge : ''
      }  ${containerClassName}`}
    >
      {label && (
        // eslint-disable-next-line jsx-a11y/label-has-associated-control
        <label className={styles.customInputLabel} style={labelStyle}>
          {label}
          {required && <span className={styles.customInputRequired}>*</span>}
          {labelRight}
        </label>
      )}
      <div
        style={{ ...inputWrapperStyle, border: error && '1px solid var(--red)' }}
        className={`${styles.customInputWrapper} ${focusStyle ? styles.customInputWrapperFocus : ''} ${
          size === 'large' ? 'wrapper-large' : ''
        } ${disabled ? styles.bgDisabled : ''} ${inputWrapperClass}`}
      >
        {left}
        <input
          {...inputProps}
          readOnly={readOnly}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          disabled={disabled}
          className={inputClassName}
          type={type}
          placeholder={placeholder}
          value={value}
          onClick={onClick}
          onChange={(e) => {
            onChange?.(e);
            if (setValue) {
              if (fieldName) {
                setValue(e.target.value, fieldName);
              } else {
                setValue(e.target.value);
              }
            }
          }}
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          onFocus={onFocus}
          style={inputStyle}
          ref={inputRef}
        />
        {right}
      </div>
      {error && (
        <div className={size === 'large' ? styles.customInputErrorMsgLarge : styles.customInputErrorMsg}>{error}</div>
      )}
    </div>
  );
}

export default React.forwardRef(InputComponent);
